import { MappedMask } from "@/store/editor/mask-utils"
import { GradientMask } from "@/core/types"
import { PropsWithChildren, useEffect, useState } from "react"
import { updateMask } from "@/store/editor/masks-state"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { usePointerDrag } from "@/utils/usePointerDrag"
import {
	AnchorPoint,
	setRotationCursor,
	unprojectPointerCoord,
} from "@/components/canvas-controls/canvas-controls-utils"
import { vec2 } from "gl-matrix"
import { CanvasControlsOverlay } from "@/components/canvas-controls/CanvasControls"
import { Handle } from "@/components/canvas-controls/Handle"
import { Line } from "@/components/canvas-controls/Line"
import { getEditor } from "@/utils/useEditor"
import { degrees } from "@/core/utils/math"

export const GradientMaskControls = (
	props: PropsWithChildren<{
		mask: MappedMask<GradientMask>
		hidden: boolean
	}>,
) => {
	const [mask, setMask] = useState(props.mask.properties)

	useEffect(() => {
		setMask(props.mask.properties)
	}, [props.mask])

	const handleUpdateMask = (properties: Partial<GradientMask>) => {
		setMask({ ...mask, ...properties })
		updateMask(props.mask, properties)
	}

	const handleUpdateMaskEnd = async () => {
		await saveCurrentAdjustmentState({
			message: `Update mask`,
			changedValue: mask.name,
		})
	}

	const bindDrag = usePointerDrag<SVGElement>({
		onPointerDown: (e) => {
			e.stopPropagation()
		},
		onDrag: (drag) => {
			const data = drag.getData(() => {
				const anchor = drag.target?.getAttribute("data-anchor") ?? "center"

				return {
					anchor: anchor as AnchorPoint | "rotate",
				}
			})

			const mask = props.mask.properties
			const pointerStart = unprojectPointerCoord(drag.startX, drag.startY)
			const pointer = unprojectPointerCoord(drag.x, drag.y)
			const delta = vec2.sub(vec2.create(), pointer, pointerStart)

			if (data.anchor === "center") {
				handleUpdateMask({
					startX: mask.startX + delta[0],
					startY: mask.startY + delta[1],
					endX: mask.endX + delta[0],
					endY: mask.endY + delta[1],
				})
			} else if (data.anchor === "rotate") {
				const newMask = handleMaskRotate(mask, {
					pointer,
					pointerStart,
				})
				handleUpdateMask(newMask)
			} else if (data.anchor === "top") {
				handleUpdateMask({
					startX: mask.startX + delta[0],
					startY: mask.startY + delta[1],
				})
			} else if (data.anchor === "bottom") {
				handleUpdateMask({
					endX: mask.endX + delta[0],
					endY: mask.endY + delta[1],
				})
			}

			if (drag.isEnd) {
				handleUpdateMaskEnd()
			}
		},
	})

	const { startX, startY, endX, endY } = mask
	const centerX = startX * 0.5 + endX * 0.5
	const centerY = startY * 0.5 + endY * 0.5
	const lineLength = 4
	const angle = getAngle(startX, startY, endX, endY)

	return (
		<CanvasControlsOverlay hidden={props.hidden}>
			<Line
				x1={startX - lineLength}
				y1={startY}
				x2={startX + lineLength}
				y2={startY}
				angle={angle}
			/>
			<Line
				x1={centerX - lineLength}
				y1={centerY}
				x2={centerX + lineLength}
				y2={centerY}
				angle={angle}
				opacity={0.5}
			/>
			<Line x1={endX - lineLength} y1={endY} x2={endX + lineLength} y2={endY} angle={angle} />
			<Handle x={startX} y={startY} anchor="top" {...bindDrag} className="z-20" />
			<Handle
				x={centerX}
				y={centerY}
				size={100}
				isTransparent
				data-anchor="rotate"
				{...bindDrag}
				onPointerEnter={(e) => {
					setRotationCursor(e, centerX, centerY)
				}}
			/>
			<Handle x={centerX} y={centerY} anchor="center" {...bindDrag} />
			<Handle x={endX} y={endY} anchor="bottom" {...bindDrag} />
			{props.children}
		</CanvasControlsOverlay>
	)
}

function getAngle(x1: number, y1: number, x2: number, y2: number) {
	const editor = getEditor()
	const [width, height] = editor.originalImageSize
	return degrees(-Math.atan2(y2 * height - y1 * height, x2 * width - x1 * width) - Math.PI / 2)
}

interface RotateHandlerOptions {
	pointer: vec2
	pointerStart: vec2
}

function rotateShape(
	startX: number,
	startY: number,
	endX: number,
	endY: number,
	angleDegrees: number,
): { finalStartX: number; finalStartY: number; finalEndX: number; finalEndY: number } {
	// Step 1: Calculate the center of the shape
	const centerX = (startX + endX) / 2
	const centerY = (startY + endY) / 2

	// Step 2: Convert the angle to radians
	const angleRadians = angleDegrees * (Math.PI / 180)

	// Step 3: Translate points to origin
	const translatedStartX = startX - centerX
	const translatedStartY = startY - centerY
	const translatedEndX = endX - centerX
	const translatedEndY = endY - centerY

	// Step 4: Rotate the points
	const rotatedStartX =
		translatedStartX * Math.cos(angleRadians) - translatedStartY * Math.sin(angleRadians)
	const rotatedStartY =
		translatedStartX * Math.sin(angleRadians) + translatedStartY * Math.cos(angleRadians)
	const rotatedEndX =
		translatedEndX * Math.cos(angleRadians) - translatedEndY * Math.sin(angleRadians)
	const rotatedEndY =
		translatedEndX * Math.sin(angleRadians) + translatedEndY * Math.cos(angleRadians)

	// Step 5: Translate points back
	const finalStartX = rotatedStartX + centerX
	const finalStartY = rotatedStartY + centerY
	const finalEndX = rotatedEndX + centerX
	const finalEndY = rotatedEndY + centerY

	return {
		finalStartX,
		finalStartY,
		finalEndX,
		finalEndY,
	}
}

function getRotatedAngle(center: vec2, start: vec2, end: vec2): number {
	const angleStart = Math.atan2(start[1] - center[1], start[0] - center[0])
	const angleEnd = Math.atan2(end[1] - center[1], end[0] - center[0])
	const angle = (angleEnd - angleStart) * (180 / Math.PI)
	return angle
}

function handleMaskRotate(mask: GradientMask, options: RotateHandlerOptions) {
	const centerX = mask.startX * 0.5 + mask.endX * 0.5
	const centerY = mask.startY * 0.5 + mask.endY * 0.5

	const angle = getRotatedAngle([centerX, centerY], options.pointerStart, options.pointer)

	const result = rotateShape(mask.startX, mask.startY, mask.endX, mask.endY, angle)

	return {
		...mask,
		startX: result.finalStartX,
		startY: result.finalStartY,
		endX: result.finalEndX,
		endY: result.finalEndY,
	}
}
